import { LEAD_FORM_EVENTS, environmentForHost, intakeStepFor, stepNameFor, type FormVariant, type InputMethod } from './lead-form-events';
import { loadPostHog } from './posthog-client';
import { redactPii } from './redact';

/*
 * The lead form funnel in PostHog, in the homepage's event names (lead-form-events.ts), sent the same way
 * by the standard form (InteractiveForm) and the guided chat (app/test-prep/chat), so the SEM form test
 * can compare them step by step:
 *
 *   lead_form_started (the first step is shown) → lead_form_step_viewed / lead_form_step_completed (per
 *   step) → lead_form_submit_attempted → lead_form_crm_accepted or lead_form_submit_failed (server,
 *   form-actions.ts)
 *
 * plus lead_form_field_invalid (validation errors) and the chat's lead_form_chat_* events (server, the
 * ask route). Each step carries the homepage chat's intake_step and, where the homepage form has one, its
 * step_name, plus the legacy panel id. Contact details, ZIP codes and free text are never sent; only the
 * choices picked. Test visitors' events also carry sem_form_variant (registered by posthog-client.ts).
 */

export { LEAD_FORM_EVENTS, type FormVariant, type InputMethod };

export type LeadFormContext = {
  variant: FormVariant;
  /** The page's subject (URL or ?vtsubject=), when it has one. */
  subject?: string | null;
};

/** A step as both forms describe it. */
export type LeadFormStep = {
  panelId: string;
  /** The legacy answer key (who-needs-help, grade-level, how_soon…). */
  key: string;
  /** 1, 2, 3… as the visitor sees it. */
  number: number;
  /** The panel type (multiple-choice, zipcode, text…) or chat input kind. */
  type: string;
  question?: string;
};

/** Answer keys whose value is never sent (contact details and location). */
const PRIVATE_KEYS = new Set(['full-name', 'email', 'phone', 'zipcode', 'consent']);

let current: LeadFormContext | null = null;
let stepsViewed = 0;
let panelCount: number | undefined;

const capture = (event: string, properties: Record<string, unknown>) => {
  if (typeof window === 'undefined' || !current) return;
  const context = current;
  void loadPostHog()
    .then((posthog) =>
      posthog.capture(event, {
        form_variant: context.variant,
        lead_form_page: window.location.pathname,
        subject: context.subject || undefined,
        chat_environment: environmentForHost(window.location.host),
        ...properties,
      }),
    )
    .catch(() => {});
};

const stepProperties = (step: LeadFormStep) => ({
  step_name: stepNameFor(step.key),
  intake_step: intakeStepFor(step.key),
  panel_id: step.panelId,
  step_number: step.number,
  step_type: step.type,
  question: step.question ? step.question.slice(0, 200) : undefined,
});

/** The form is on screen; its funnel starts with its first step (lead_form_started). */
export function trackLeadFormShown(context: LeadFormContext, details: { panelCount?: number } = {}) {
  current = context;
  stepsViewed = 0;
  panelCount = details.panelCount;
}

/** A step is shown; the first one starts the form, as on the homepage form. */
export function trackLeadFormStepViewed(step: LeadFormStep) {
  if (stepsViewed === 0) capture(LEAD_FORM_EVENTS.started, { ...stepProperties(step), panel_count: panelCount });
  stepsViewed += 1;
  capture(LEAD_FORM_EVENTS.stepViewed, stepProperties(step));
}

/** An answer, with its value for the choice questions only. */
export function trackLeadFormStepCompleted(step: LeadFormStep, details: { answer?: string; inputMethod?: InputMethod; typedText?: string } = {}) {
  const answer = details.answer !== undefined && !PRIVATE_KEYS.has(step.key) ? details.answer.slice(0, 200) : undefined;
  capture(LEAD_FORM_EVENTS.stepCompleted, {
    ...stepProperties(step),
    answer,
    input_method: details.inputMethod,
    // What the visitor typed into the chat's message box when it named an option.
    typed_text: details.typedText ? redactPii(details.typedText, 300) : undefined,
  });
}

/** A validation error on a field (the label the standard form logs: invalid_email, invalid_full_name…). */
export function trackLeadFormFieldInvalid(panelId: string, invalidLabel: string) {
  capture(LEAD_FORM_EVENTS.fieldInvalid, { panel_id: panelId, invalid_label: invalidLabel });
}

export function trackLeadFormSubmitAttempted(details: { stepCount: number }) {
  capture(LEAD_FORM_EVENTS.submitAttempted, { step_count: details.stepCount });
}

/** The visitor's PostHog ids, for server-side events about them (the chat's messages). */
export async function leadFormPostHogIds(): Promise<{ distinctId?: string; sessionId?: string }> {
  try {
    const posthog = await loadPostHog();
    return { distinctId: posthog.get_distinct_id?.() || undefined, sessionId: posthog.get_session_id?.() || undefined };
  } catch {
    return {};
  }
}
